import axios from 'axios';

const BASE_URL = process.env.REACT_APP_API_BASE_URL as string;

if (!BASE_URL) {
    throw new Error('BASE_URL is not defined in the environment variables.');
}

export interface DictionaryItem {
    label: string;
    value: string;
}

export interface DictionaryName {
    name: string;
    code: string;
}

/**
 * Sanitize a string value by trimming whitespace and stripping HTML tags.
 */
export function sanitizeString(value: string): string {
    return value.trim().replace(/<[^>]*>/g, '');
}

/**
 * Deep-sanitize all string values in an object.
 */
export function sanitizeFormData<T>(data: T): T {
    if (typeof data === 'string') {
        return sanitizeString(data) as unknown as T;
    }
    if (Array.isArray(data)) {
        return data.map(item => sanitizeFormData(item)) as unknown as T;
    }
    if (data !== null && typeof data === 'object') {
        const sanitized: Record<string, unknown> = {};
        for (const [key, value] of Object.entries(data as Record<string, unknown>)) {
            sanitized[key] = sanitizeFormData(value);
        }
        return sanitized as T;
    }
    return data;
}

const api = axios.create({
    baseURL: BASE_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});
async function rawGet<T>(url: string): Promise<T> {
    const response = await api.get<T>(url);
    return response.data;
}
// Uniwersalna funkcja do bezpiecznego pobierania danych
async function safeGet<T>(url: string): Promise<T> {
    try {
        const response = await api.get<T>(url);
        return response.data;
    } catch {
        return [] as unknown as T;
    }
}

export interface ContactDetails {
    firstName: string;
    lastName: string;
    email: string;
    phoneNo: string;
    companyName?: string;
}

export interface Token {
    value: string;
    url: string;
    validTill: string;
}

export interface FormResponse {
    formType: "PERSONAL" | "B2B";
    status: string;
    contactDetails: ContactDetails;
    token: Token;
}

export interface FormDataB2B {
    formType: "B2B";
    status: "SUBMITTED";
    contactDetails: ContactDetails;
    token: Token;
    name: string;
    taxId: string;
    statisticalId: string;
    legalForm: string;
    registeredOfficeAddress: {
        country: string;
        voivodeship: string;
        county: string;
        municipality: string;
        street: string;
        houseNumber: string;
        apartmentNumber?: string;
        postalCode: string;
        city: string;
    };
    representativePerson: {
        firstName: string;
        lastName: string;
        position: string;
    };
    settlementData: {
        accountNumber: string;
        bankName: string;
    };
    declarationFooter: {
        date: string;
    };
}

export interface TaxOfficeDict {
    value: string;
    label: string;
}

export interface FormDataPerson {
    formType: "PERSONAL";
    status: "SUBMITTED";
    contactDetails: ContactDetails;
    token: Token | '';
    personalData: {
        lastName: string;
        firstName: string;
        middleName: string;
        pesel: string;
        dateOfBirth: string;
        citizenship: string[];
        taxOfficeDict: TaxOfficeDict;
        // taxOffice: string;
    };
    residentialAddress: {
        country: string;
        voivodeship: string;
        county: string;
        municipality: string;
        street: string;
        houseNumber: string;
        apartmentNumber?: string;
        postalCode: string;
        city: string;
    };
    // Opcjonalne — sekcja oświadczeń ZUS bywa wyłączona (patrz ZUS_STATEMENTS_ENABLED
    // w FormPagePerson). Backend przyjmuje formularz bez tego pola.
    declaration?: {
        socialInsuranceFromOtherContract?: {
            entityName: string;
            salaryAboveMinimum: boolean;
        };
        contractOfMandateWithOtherEntity?: {
            nameAndAddress: string;
            salaryAboveMinimum: boolean;
            periodFrom: string;
            periodTo: string;
        };
        retired: boolean;
        pensioner: boolean;
        student: boolean;
        disability?: {
            haveDisability: boolean;
            degree: "lekki" | "umiarkowany" | "znaczny";
        };
        voluntaryHealthInsurance: boolean;
        businessActivity: boolean;
    };
    settlementData: {
        accountNumber: string;
        bankName: string;
    };
    declarationFooter: {
        date: string;
    };
}
export interface NipData {
    name: string;
    regon: string;
    statusVat: string;
    workingAddress: string;
    street: string;
    houseNumber: string;
    postalCode: string;
    city: string;
    legalForm?: string;
}

export const dictionaryService = {
    postFormB2B: (token: string, data: FormDataB2B) =>
        api.put(`/be/api/forms/${token}`, data),
    postFormPerson: (token: string, data: FormDataPerson) =>
        api.put(`/be/api/forms/${token}`, data),
    getFormByToken: (token: string) => rawGet<FormResponse>(`/be/api/forms/${token}`),
    getCitizenships: () => safeGet<DictionaryItem[]>('/be/api/dictionary/citizenship'),
    getTaxOffices: () => safeGet<DictionaryItem[]>('/be/api/dictionary/tax-office'),
    getVoivodeships: () => safeGet<DictionaryName[]>('/be/api/dictionary/address/voivodeships'),
    getCounties: (voivodeshipKey: string) =>
        safeGet<DictionaryName[]>(`/be/api/dictionary/address/voivodeships/${voivodeshipKey}/counties`),
    getMunicipalities: (voivodeshipKey: string, countyKey: string) =>
        safeGet<DictionaryName[]>(`/be/api/dictionary/address/voivodeships/${voivodeshipKey}/counties/${countyKey}/municipalities`),
    getNipData: (nip: string) => rawGet<NipData>(`/be/api/nip/${nip}`),
};

export default dictionaryService;